"use client";
import "./footer.css";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { FaMobileAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { footerSections, socialLinks } from "src/utils/mock/footerData";

const currentYear = new Date().getFullYear();

export default function AppFooter() {
  const footerRef = useRef(null);

  useEffect(() => {
    let ctx;
    let ScrollTrigger;

    const initGSAP = async () => {
      try {
        const { gsap } = await import("gsap");
        const ST = await import("gsap/ScrollTrigger");

        ScrollTrigger = ST.ScrollTrigger;
        gsap.registerPlugin(ScrollTrigger);

        ctx = gsap.context(() => {
          gsap.from(".footer-main-grid > div", {
            opacity: 0,
            y: 20,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: footerRef.current,
              start: "top 85%",
              once: true,
            },
          });
        }, footerRef);

        requestAnimationFrame(() => {
          ScrollTrigger.refresh();
        });
      } catch (error) {
        console.warn("GSAP Footer Animation Error:", error);
      }
    };

    initGSAP();

    return () => {
      if (ScrollTrigger) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      }
      ctx?.revert();
    };
  }, []);

  return (
    <footer ref={footerRef} className="footer-root">
      <div className="footer-mesh" aria-hidden="true" />

      <div className="footer-container">
        <div className="footer-main-grid">
          <div className="footer-brand-col">
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Image src="/images/Tycho_Logo_new.png" alt="Tycho Technologies" width={90} height={10} style={{ objectFit: "contain" }} priority />
              <h3 className="font-display text-3xl leading-[1.20] text-text-primary flex flex-col tracking-widest tracking-[4px]">
                TYCHO
                <span className="bg-gradient-to-r from-[var(--c-brand)] to-[var(--c-accent)] bg-clip-text text-transparent text-[14px] tracking-[3px]">Technologies</span>
              </h3>
            </div>

            <p className="footer-tagline">
              Delivering innovative web, mobile, and digital solutions that help businesses achieve scalable growth, better performance, and long-term success.
            </p>
            <div className="social-icons">
              {socialLinks.map((item, i) => (
                <a key={i} href={item.href} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label={item.label}>
                  {item.icon}
                </a>
              ))}
            </div>
          </div>

          {footerSections.map((section) => (
            <div key={section.title} className="footer-link-col">
              <h4 className="footer-col-heading">{section.title}</h4>
              <ul className="footer-link-list">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link href={link.href} className="footer-link">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="footer-contact-card">
            <h3 className="card-heading">
              <span className="heading-top">
                <span className="text-pink-purple">Let's </span>
                <span className="text-purple-blue">Build</span>
              </span>
              <span className="heading-bottom">
                <span className="text-pink-purple">Something </span>
                <span className="text-purple-blue">Great.</span>
              </span>
            </h3>

            <p className="card-sub">Ready to transform your digital presence? We're one message away.</p>

            <div className="contact-rows-grid">
              <a
                href="https://www.google.com/maps/place/Tycho+Technologies+%7C+Mobile+Apps,+OTT,+ROKU+%26+Web+Development+Company+in+India/@28.6297619,77.3766279,17z/data=!3m1!4b1!4m14!1m7!3m6!1s0x390ce40a1a48d491:0x773396a1d869a95f!2sTycho+Technologies+%7C+Mobile+Apps,+OTT,+ROKU+%26+Web+Development+Company+in+India!8m2!3d28.6297572!4d77.3792028!16s%2Fg%2F11g889jrhd!3m5!1s0x390ce40a1a48d491:0x773396a1d869a95f!8m2!3d28.6297572!4d77.3792028!16s%2Fg%2F11g889jrhd?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item-row"
              >
                <span className="icon-box">
                  <FaMapMarkerAlt size={23} />
                </span>
                <div>
                  <span className="item-text-main">Office No.305, H-32, Sector-63</span>
                  <span className="item-text-sub">Noida, UP 201301, India</span>
                </div>
              </a>

              <div className="phone-grid-row">
                <a href="tel:+919717699480" className="contact-item-row">
                  <span className="icon-box">
                    <FaMobileAlt size={23} />
                  </span>
                  <div>
                    <span className="item-text-main">+91 9717699480</span>
                    <span className="item-text-sub">Business Enquiry</span>
                  </div>
                </a>
                <a href="tel:+919205671059" className="contact-item-row">
                  <span className="icon-box">
                    <FaMobileAlt size={23} />
                  </span>
                  <div>
                    <span className="item-text-main">+91 9205671059</span>
                    <span className="item-text-sub">Support</span>
                  </div>
                </a>
              </div>

              <a href="mailto:contact@tychotechnologies.com" className="contact-item-row">
                <span className="icon-box">
                  <FaEnvelope size={23} />
                </span>
                <span className="item-text-main">contact@tychotechnologies.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">© {currentYear} Tycho Technologies Pvt. Ltd. — All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
